import React, { useState, useRef, useCallback } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import translations from '../lang/en/general.json';

interface ImageCropDialogProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  onSave: (crop: Crop, croppedBlob: Blob) => void;
}

const ImageCropDialog: React.FC<ImageCropDialogProps> = ({ open, onClose, imageUrl, onSave }) => {
  // Initialize crop state according to the interface
  const [crop, setCrop] = useState<Crop>({ x: 0, y: 0, width: 50, height: 50, unit: '%' });
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const onImageLoaded = (event: React.SyntheticEvent<HTMLImageElement>) => {
    imageRef.current = event.currentTarget;
  };

  const onCropComplete = (crop: Crop) => {
    setCompletedCrop(crop);
  };

  const getCroppedImg = useCallback(async (): Promise<Blob> => {
    if (!completedCrop || !imageRef.current || !completedCrop.width || !completedCrop.height) {
      throw new Error('Crop is not complete or invalid');
    }
    const canvas = document.createElement('canvas');
    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas context not available');
    }
    ctx.drawImage(
      imageRef.current,
      (completedCrop.x || 0) * scaleX,
      (completedCrop.y || 0) * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create Blob'));
      }, 'image/jpeg');
    });
  }, [completedCrop]);

  const handleSave = async () => {
    if (completedCrop) {
      try {
        const croppedBlob = await getCroppedImg();
        onSave(completedCrop, croppedBlob);
        onClose();
      } catch (error) {
        console.error('Error cropping image:', error);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Crop Image</DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <ReactCrop
            crop={crop}
            onChange={(newCrop) => setCrop(newCrop)}
            onComplete={onCropComplete}
          >
            <img src={imageUrl} onLoad={(event) => onImageLoaded(event)} alt="Crop me" />
          </ReactCrop>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {translations.form.buttons.cancel}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {translations.form.buttons.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
